import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import {
  type AdminUpdateFeedbackRequest,
  type AgentFeedbackRecord,
} from "@/shared/domain/agentThread";
import { PaginatedResponse } from "@/shared/http";

import type {
  FeedbackCountsBySession,
  GetAdminFeedbackOptions,
} from "../AdminService";

// Derived from the service shape so the two cannot drift. The hook supplies
// ``abortSignal`` itself from the query's signal, so callers should not pass
// one through the filters.
export type AdminFeedbackFilters = Omit<GetAdminFeedbackOptions, "abortSignal">;

export function useAdminFeedback(filters: AdminFeedbackFilters) {
  const { adminService } = useDomainServices();

  return useQuery<PaginatedResponse<AgentFeedbackRecord>, Error>({
    queryKey: [
      "adminFeedback",
      filters.sentiment,
      filters.category,
      filters.orgId,
      filters.sessionId,
      filters.createdBy,
      filters.resolved,
      filters.hasNotes,
      filters.hasErrors,
      filters.isCustomer,
      filters.createdAfter,
      filters.createdBefore,
      filters.pageToken,
      filters.limit,
    ],
    queryFn: ({ signal }) =>
      adminService.getAdminFeedback({ ...filters, abortSignal: signal }),
    staleTime: 30000,
  });
}

export function useAdminFeedbackCountsForAgentThreads(filters: {
  orgId?: string;
  createdBy?: string;
  sessionId?: string;
}) {
  const { adminService } = useDomainServices();

  // ``||`` (truthy chain) rather than ``??`` (nullish-only) so an empty
  // string from a cleared-filter UI state correctly resolves to "no filter
  // set" — ``"" ?? next`` short-circuits on the empty string and would
  // mistakenly enable the query with no real filter, which the service
  // method then rejects.
  const enabled = Boolean(
    filters.orgId || filters.createdBy || filters.sessionId,
  );

  return useQuery<FeedbackCountsBySession, Error>({
    queryKey: [
      "adminFeedbackCounts",
      filters.orgId,
      filters.createdBy,
      filters.sessionId,
    ],
    queryFn: ({ signal }) =>
      adminService.getAdminFeedbackCountsForAgentThreads(filters, {
        abortSignal: signal,
      }),
    enabled,
    staleTime: 30000,
  });
}

interface UpdateAdminFeedbackParams {
  feedbackId: string;
  request: AdminUpdateFeedbackRequest;
}

/**
 * Admin mutation for triage fields (label/note/resolved).
 *
 * Invalidates the admin feedback listing on success so the table reflects the
 * new state without a manual refresh.
 */
export function useUpdateAdminFeedback() {
  const { adminService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<AgentFeedbackRecord, Error, UpdateAdminFeedbackParams>({
    mutationFn: async ({ feedbackId, request }: UpdateAdminFeedbackParams) =>
      adminService.updateAdminFeedback(feedbackId, request),
    onSuccess: async () => {
      // Invalidate both the listing and the per-thread counts. The counts
      // query reads ``resolved`` and ``positive``/``negative`` totals
      // indirectly — admin updates don't change sentiment, but they DO
      // change resolved-state, so the unresolved-queue derivative needs to
      // recompute.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["adminFeedback"] }),
        queryClient.invalidateQueries({ queryKey: ["adminFeedbackCounts"] }),
      ]);
    },
  });
}
